import {
  Box,
  Card,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import React from "react";
import HeaderWithBackButton from "../components/Common/HeaderWithBackButton.tsx";
import QueryListCard from "../components/Query/QueryListCard.tsx";
import Api from "../Interceptor/Api/index.ts";
import TitleWithBody from "../components/Common/TitleWithBody.tsx";
import { ComponentConfig } from "../Config/ComponentConfig.js";
import { Colors, STATUS_STYLES } from "../Constants/Colors.js";
import CustomVerticalStepper from "../components/Common/VerticalStepper.tsx";
import PrimaryButton from "../components/Common/PrimaryButton.tsx";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Query } from "../Types/index.ts";
import { toast, ToastContainer } from "react-toastify";
import { WebRoutes } from "../Routes/WebRoutes.js";
import { ReopenQueryDialog } from "../components/Appointment/ReopenQueryDialog.tsx";

interface Steps {
  label: string;
  date?: string;
}

export default function QueryDetails() {
  const params = useParams();
  const navigate = useNavigate();
  const { id } = params;
  const [isLoading, setIsLoading] = React.useState(false);
  const [queryData, setQueryData] = React.useState<Query>({} as Query);
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const fetchAllQueries = async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      const response = await Api.appointment.getQueryDetails(id);
      setQueryData(response);
    } catch (error) {
      console.error("Error fetching queries:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFeedbackClick = async (value: string, route: boolean = false) => {
    try {
      const data = {
        id: queryData?.id,
        feedback: value,
      };
      await Api.appointment.submitQueryFeedback(data);
      await fetchAllQueries();
      if (value === "satisfied") {
        toast.success("Thank you for your feedback!");
      } else {
        if (route) {
          navigate(WebRoutes.QUERY_FEEDBACK, {
            state: {
              previousQueryId: queryData?.id,
              issue: queryData?.issue_type,
            },
          });
        } else {
          toast.success("Query reopened successfully!");
        }
      }
    } catch (error: Error | any) {
      console.log("error type");
      toast.error(error?.message);
      console.error("Error submitting feedback:", error);
    }
  };

  const getActiveStep = () => {
    const status = queryData?.status?.toLowerCase();
    if (status === "closed") {
      return 2;
    }
    if (status === "resolved" && queryData?.customer_feedback) {
      return 2;
    } else if (status === "resolved") {
      return 1;
    }
    return 0;
  };

  const getFinalStepLabel = () => {
    if (queryData?.customer_feedback === "unsatisfied") {
      return "Patient disapproved, query opened again";
    }
    return "Approved by patient";
  };

  const getAllSteps = () => {
    const steps: Steps[] = [
      { label: "Query registered with team", date: queryData?.created_at },
      { label: "Team shared the resolution" },
      { label: getFinalStepLabel() },
    ];
    if (queryData?.status?.toLowerCase() === "resolved") {
      const currentStep = getActiveStep();
      steps[currentStep].date = queryData?.updated_at;
    }
    return steps;
  };

  React.useEffect(() => {
    fetchAllQueries();
  }, []);
  const location = useLocation();

  React.useEffect(() => {
    toast.dismiss(); // close all toasts on route change
  }, [location]);

  const documentUrl = queryData?.customer_document?.document_url;
  const kynoDocumentUrl = queryData?.team_document?.document_url;
  const showDivider =
    queryData?.customer_remarks || queryData?.solution_remarks || documentUrl;
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  return (
    <Box
      className="form-container-v1"
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        alignItems: "flex-start",
        gap: 2,
        padding: "0px",
      }}
    >
      <HeaderWithBackButton title="Query Details" />
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          width: "100%",
        }}
      >
        <Box
          display="flex"
          width="100%"
          flexDirection="column"
          paddingX="16px"
          gap="10px"
        >
          <QueryListCard shouldTap={false} data={queryData} />
          {queryData?.status?.toLowerCase() === "open" && (
            <Typography
              sx={{
                width: "100%",
                textAlign: "center",
              }}
              color={Colors.primaryTextColor}
              fontSize={ComponentConfig.fonts.bodyRegular.fontSize}
              fontWeight={ComponentConfig.fonts.bodyBold.fontWeight}
            >
              We have received your query and will get back to you soon.
            </Typography>
          )}
          <Divider
            sx={{
              width: "100%",
              backgroundColor: Colors.greenBorderColor,
              height: "1px",
            }}
          />
          {queryData?.customer_remarks && (
            <TitleWithBody
              title="Your Remarks"
              subtitle={queryData?.customer_remarks}
              titleStyle={{
                fontSize: ComponentConfig.fonts.smallRegular.fontSize,
                fontWeight: ComponentConfig.fonts.smallRegular.fontWeight,
                color: Colors.secondaryTextColor,
              }}
              subtitleStyle={{
                fontSize: ComponentConfig.fonts.bodyRegular.fontSize,
                fontWeight: ComponentConfig.fonts.bodyRegular.fontWeight,
                color: Colors.primaryTextColor,
              }}
            />
          )}
          {documentUrl && (
            <Card
              elevation={0}
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {/* Document Title */}
              <Box display="flex" alignItems="center" gap="10px">
                <img
                  src="/icons/document_pdf.svg"
                  alt="PDF Icon"
                  style={{
                    width: "24px",
                  }}
                />
                <Typography
                  color={Colors.primaryTextColor}
                  fontSize={ComponentConfig.fonts.bodyRegular.fontSize}
                  fontWeight={ComponentConfig.fonts.bodyRegular.fontWeight}
                >
                  Document
                </Typography>
              </Box>

              {/* Primary Button with custom title and action */}
              <PrimaryButton
                text="Download"
                fullWidth={false}
                textClassName="body-regular"
                backgroundColor={Colors.greenBorderColor}
                textColor={Colors.white}
                onTap={() => {
                  window.open(documentUrl, "_blank");
                }}
                sx={{
                  padding: "14px 20px",
                }}
                endIcon={
                  <img
                    src="/icons/download_icon.svg"
                    alt="Custom Icon"
                    style={{ width: 20, height: 20 }}
                  />
                }
              />
            </Card>
          )}
          {queryData?.solution_remarks && (
            <TitleWithBody
              title="Kyno's Remarks"
              subtitle={queryData?.solution_remarks || ""}
              titleStyle={{
                fontSize: ComponentConfig.fonts.smallRegular.fontSize,
                fontWeight: ComponentConfig.fonts.smallRegular.fontWeight,
                color: Colors.secondaryTextColor,
              }}
              subtitleStyle={{
                fontSize: ComponentConfig.fonts.bodyRegular.fontSize,
                fontWeight: ComponentConfig.fonts.bodyRegular.fontWeight,
                color: Colors.primaryTextColor,
              }}
            />
          )}
          {kynoDocumentUrl && (
            <Card
              elevation={0}
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {/* Document Title */}
              <Box display="flex" alignItems="center" gap="10px">
                <img
                  src="/icons/document_pdf.svg"
                  alt="PDF Icon"
                  style={{
                    width: "24px",
                  }}
                />
                <Typography
                  color={Colors.primaryTextColor}
                  fontSize={ComponentConfig.fonts.bodyRegular.fontSize}
                  fontWeight={ComponentConfig.fonts.bodyRegular.fontWeight}
                >
                  Kyno Document
                </Typography>
              </Box>

              {/* Primary Button with custom title and action */}
              <PrimaryButton
                text="Download"
                fullWidth={false}
                textClassName="body-regular"
                backgroundColor={Colors.greenBorderColor}
                textColor={Colors.white}
                onTap={() => {
                  window.open(documentUrl, "_blank");
                }}
                sx={{
                  padding: "14px 20px",
                }}
                endIcon={
                  <img
                    src="/icons/download_icon.svg"
                    alt="Custom Icon"
                    style={{ width: 20, height: 20 }}
                  />
                }
              />
            </Card>
          )}
          {showDivider && (
            <Divider
              sx={{
                width: "100%",
                backgroundColor: Colors.greenBorderColor,
                height: "1px",
              }}
            />
          )}
          <CustomVerticalStepper
            querySteps={getAllSteps()}
            activeStep={getActiveStep()}
          />
          `
        </Box>
      </Box>
      {queryData?.status?.toLowerCase() === "resolved" &&
        !queryData?.customer_feedback && (
          <Box
            width="100%"
            bgcolor="#179d820f"
            padding="20px"
            display={"flex"}
            justifyContent={"space-between"}
          >
            <Typography
              sx={{
                fontSize: ComponentConfig.fonts.bodyRegular.fontSize,
                fontWeight: ComponentConfig.fonts.bodyRegular.fontWeight,
                color: Colors.primaryTextColor,
              }}
            >
              Was your query resolved?
            </Typography>
            <Typography
              component={"span"}
              onClick={async () => {
                await handleFeedbackClick("satisfied");
              }}
              sx={{
                fontSize: ComponentConfig.fonts.subtitleMedium.fontSize,
                fontWeight: ComponentConfig.fonts.subtitleMedium.fontWeight,
                color: STATUS_STYLES.completed.textColor,
                cursor: "pointer",
              }}
            >
              Yes
            </Typography>
            <Typography
              component={"span"}
              onClick={() => {
                setDialogOpen(true);
              }}
              sx={{
                fontSize: ComponentConfig.fonts.subtitleMedium.fontSize,
                fontWeight: ComponentConfig.fonts.subtitleMedium.fontWeight,
                color: STATUS_STYLES.cancelled.textColor,
                cursor: "pointer",
              }}
            >
              No
            </Typography>
          </Box>
        )}
      <ToastContainer />
      <ReopenQueryDialog
        open={dialogOpen}
        onClose={async () => {
          setDialogOpen(false);
          await handleFeedbackClick("unsatisfied");
        }}
        onConfirm={async () => {
          await handleFeedbackClick("unsatisfied", true);
        }}
      />
    </Box>
  );
}
